import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
    Upload,
    X,
    Save,
    Send,
    ArrowLeft,
    Sparkles,
    AlertCircle,
    Loader2
} from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { api } from "../../services/api";
import { hasPosition } from "../../global-components/src/utils/auth";
import { BUCKET_PACOCA_URL } from "../../global-components/src/utils/vars";
import BlogNavbar from "./components/BlogNavbar";
import "./blog-editor.css";

// Custom upload adapter that sends images to /blog/upload-image
class BlogUploadAdapter {
    private loader: any;
    private token: string | null;

    constructor(loader: any, token: string | null) {
        this.loader = loader;
        this.token = token;
    }

    upload(): Promise<{ default: string }> {
        return this.loader.file.then(async (file: File) => {
            const safeFile = await convertToJpegIfWebp(file);
            const formData = new FormData();
            formData.append("image", safeFile);
            return api(this.token ?? "")
                .post("/blog/upload-image", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                })
                .then((res: any) => ({ default: `${BUCKET_PACOCA_URL}/${res.data.path}` }));
        });
    }

    abort() {}
}

function blogUploadPlugin(editor: any) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
        return new BlogUploadAdapter(loader, (editor as any)._blogToken ?? null);
    };
}

/**
 * If the given file is a WebP image, re-encode it as JPEG via canvas.
 * Returns a new JPEG File; for all other types returns the original file.
 * This is needed because the server's GD extension lacks WebP support.
 */
async function convertToJpegIfWebp(file: File): Promise<File> {
    if (file.type !== "image/webp") return file;
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d")!.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob(
                (blob) => {
                    const jpegName = file.name.replace(/\.webp$/i, ".jpg");
                    resolve(blob ? new File([blob], jpegName, { type: "image/jpeg" }) : file);
                },
                "image/jpeg",
                0.92
            );
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
        img.src = url;
    });
}

/** Strip HTML tags to count plain-text length for validation */
function stripHtml(html: string): string {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}

/**
 * Normalise a cover_image value to a bucket-relative path.
 * Old DB records store full CDN URLs; new records store paths only.
 * Returns null when the value is empty.
 */
function toCoverPath(value: string | null | undefined): string | null {
    if (!value) return null;
    // Already a relative path (no scheme)
    if (!value.startsWith("http")) return value;
    // Strip any known CDN prefix and return the remainder as a path
    const marker = "/pacoca-prod-files/";
    const idx = value.indexOf(marker);
    if (idx !== -1) return value.slice(idx + marker.length);
    const devMarker = "/pacoca-dev-files/";
    const idx2 = value.indexOf(devMarker);
    if (idx2 !== -1) return value.slice(idx2 + devMarker.length);
    // Unknown full URL — return as-is so it still renders (full URL passed to src works fine)
    return value;
}

const BlogEditor: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id?: string }>();
    const isEditing = Boolean(id);
    const { token, user, isSuper } = useAuth();
    const { setMessage } = useMessage();
    const navigate = useNavigate();

    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [content, setContent] = useState("");
    const [coverImage, setCoverImage] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [savingStatus, setSavingStatus] = useState<"draft" | "published" | null>(null);
    const [fetchingPost, setFetchingPost] = useState(isEditing);
    const [uploadingCover, setUploadingCover] = useState(false);

    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const editorRef = useRef<any>(null);

    const canAutoApprove = Boolean(
        isSuper ||
        hasPosition(user) ||
        user?.stamps?.includes("brown") ||
        user?.permissions?.includes("approve_blog_post")
    );

    useEffect(() => {
        if (!token) {
            navigate("/login");
            return;
        }
        if (isEditing && id) {
            fetchPostToEdit(id);
        }
    }, [id, token]);

    const fetchPostToEdit = async (postId: string) => {
        try {
            setFetchingPost(true);
            const res = await api(token).get(`/blog/my-posts`);
            const found = res.data?.data?.find((p: any) => String(p.id) === String(postId));
            if (found) {
                setTitle(found.title || "");
                setSummary(found.summary || "");
                setContent(found.content || "");
                setCoverImage(toCoverPath(found.cover_image));
            } else {
                setMessage(t("blog.editor.validation.loadError"), "error");
                navigate("/blog/gerenciar");
            }
        } catch (err: any) {
            setMessage(t("blog.editor.validation.loadError"), "error");
            navigate("/blog/gerenciar");
        } finally {
            setFetchingPost(false);
        }
    };

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.files?.[0];
        if (!raw) return;

        const file = await convertToJpegIfWebp(raw);
        const formData = new FormData();
        formData.append("image", file);

        try {
            setUploadingCover(true);
            const res = await api(token).post("/blog/upload-image", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (res.data?.path) {
                setCoverImage(res.data.path);
                setMessage(t("blog.editor.validation.coverSuccess"), "success");
            }
        } catch (err: any) {
            setMessage(t("blog.editor.validation.coverError"), "error");
        } finally {
            setUploadingCover(false);
        }
    };

    const handleSave = async (status: "draft" | "published") => {
        const trimmedTitle = title.trim();
        const trimmedSummary = summary.trim();
        const plainContent = stripHtml(content).trim();

        if (trimmedTitle.length < 5) {
            setMessage(t("blog.editor.validation.titleMin"), "error");
            return;
        }

        if (trimmedTitle.length > 150) {
            setMessage(t("blog.editor.validation.titleMax"), "error");
            return;
        }

        if (trimmedSummary && trimmedSummary.length < 10) {
            setMessage(t("blog.editor.validation.summaryMin"), "error");
            return;
        }

        if (trimmedSummary.length > 300) {
            setMessage(t("blog.editor.validation.summaryMax"), "error");
            return;
        }

        if (plainContent.length < 100) {
            setMessage(t("blog.editor.validation.contentMin", { current: plainContent.length }), "error");
            return;
        }

        if (content.length > 50000) {
            setMessage(t("blog.editor.validation.contentMax"), "error");
            return;
        }

        try {
            setLoading(true);
            setSavingStatus(status);
            const payload = {
                title: title.trim(),
                summary: summary.trim() || null,
                content,
                cover_image: coverImage,
                status
            };

            if (isEditing && id) {
                await api(token).put(`/blog/${id}`, payload);
                setMessage(t("blog.editor.validation.updateSuccess"), "success");
                navigate("/blog/gerenciar");
            } else {
                const res = await api(token).post("/blog", payload);
                setMessage(res.data?.message || t("blog.editor.validation.createSuccess"), "success");
                if (status === "published" && res.data?.auto_approved) {
                    navigate(`/blog/artigo/${res.data.post.slug}`);
                } else {
                    navigate("/blog/gerenciar");
                }
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || t("blog.editor.validation.loadError");
            setMessage(msg, "error");
        } finally {
            setLoading(false);
            setSavingStatus(null);
        }
    };

    const plainLength = stripHtml(content).trim().length;

    if (fetchingPost) {
        return (
            <div className="blog-editor-loading">
                <p>{t("blog.loading")}</p>
            </div>
        );
    }

    return (
        <div className="blog-editor-page">
            <Helmet>
                <title>{isEditing ? t("blog.editor.editTitle") : t("blog.editor.newTitle")} – {t("blog.title")}</title>
            </Helmet>

            <BlogNavbar />

            <div className="blog-editor-header">
                <div className="blog-editor-header-inner">
                    <Link to="/blog/gerenciar" className="blog-editor-back-btn">
                        <ArrowLeft size={16} />
                        <span>{t("blog.previous")}</span>
                    </Link>
                </div>
            </div>

            <main className="blog-editor-main">
                <div className="blog-editor-container">
                    {/* Role Notice */}
                    <div className={`blog-status-banner ${canAutoApprove ? "banner-approved" : "banner-pending"}`}>
                        {canAutoApprove ? (
                            <>
                                <Sparkles size={18} className="banner-icon" />
                                <div>{t("blog.editor.autoApprovedBanner")}</div>
                            </>
                        ) : (
                            <>
                                <AlertCircle size={18} className="banner-icon" />
                                <div>{t("blog.editor.moderationBanner")}</div>
                            </>
                        )}
                    </div>

                    {/* Cover Image */}
                    <div className="blog-cover-section">
                        {coverImage ? (
                            <div className="blog-cover-preview-wrapper">
                                <img src={coverImage!.startsWith("http") ? coverImage! : `${BUCKET_PACOCA_URL}/${coverImage}`} alt="Capa" className="blog-cover-preview-img" />
                                <button
                                    type="button"
                                    onClick={() => setCoverImage(null)}
                                    disabled={uploadingCover}
                                    className="blog-cover-remove-btn"
                                    title={t("blog.editor.removeCover")}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <div
                                className={`blog-cover-dropzone${uploadingCover ? " blog-cover-dropzone--loading" : ""}`}
                                onClick={() => !uploadingCover && coverInputRef.current?.click()}
                            >
                                {uploadingCover ? <Loader2 size={24} className="blog-cover-spinner" /> : <Upload size={24} />}
                                <span>{uploadingCover ? t("blog.editor.coverUploading") : t("blog.editor.coverDropzone")}</span>
                                <input
                                    type="file"
                                    ref={coverInputRef}
                                    onChange={handleCoverUpload}
                                    accept="image/*"
                                    disabled={uploadingCover}
                                    style={{ display: "none" }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Title & Summary */}
                    <div className="blog-input-group">
                        <input
                            type="text"
                            placeholder={t("blog.editor.titlePlaceholder")}
                            value={title}
                            maxLength={150}
                            onChange={(e) => setTitle(e.target.value)}
                            className="blog-editor-title-input"
                        />
                        <div className="blog-input-counter">
                            <span className={title.trim().length > 0 && title.trim().length < 5 ? "counter-invalid" : ""}>
                                {title.length} / 150
                            </span>
                        </div>
                    </div>

                    <div className="blog-input-group">
                        <input
                            type="text"
                            placeholder={t("blog.editor.summaryPlaceholder")}
                            value={summary}
                            maxLength={300}
                            onChange={(e) => setSummary(e.target.value)}
                            className="blog-editor-summary-input"
                        />
                        <div className="blog-input-counter">
                            <span className={summary.trim().length > 0 && summary.trim().length < 10 ? "counter-invalid" : ""}>
                                {summary.length} / 300
                            </span>
                        </div>
                    </div>

                    {/* CKEditor */}
                    <div className="blog-ckeditor-wrapper">
                        <CKEditor
                            editor={ClassicEditor as any}
                            data={content}
                            onReady={(editor: any) => {
                                editorRef.current = editor;
                                // Inject token into editor instance for upload adapter
                                editor._blogToken = token;
                                editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) =>
                                    new BlogUploadAdapter(loader, token);
                            }}
                            onChange={(_event: any, editor: any) => {
                                setContent(editor.getData());
                            }}
                            config={{
                                placeholder: t("blog.editor.contentPlaceholder"),
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link", "|",
                                    "bulletedList", "numberedList", "blockQuote", "|",
                                    "imageUpload", "insertTable", "mediaEmbed", "|",
                                    "undo", "redo"
                                ]
                            }}
                        />
                        <div className="blog-textarea-footer">
                            <span className={plainLength > 0 && plainLength < 100 ? "counter-invalid" : "counter-valid"}>
                                {plainLength < 100
                                    ? t("blog.editor.minCharacters", { current: plainLength })
                                    : t("blog.editor.charactersCount", { current: content.length.toLocaleString() })}
                            </span>
                        </div>
                    </div>

                    {/* Bottom action buttons */}
                    <div className="blog-editor-bottom-actions">
                        <button
                            type="button"
                            onClick={() => handleSave("draft")}
                            disabled={loading}
                            className="blog-editor-btn-secondary"
                        >
                            {loading && savingStatus === "draft" ? (
                                <Loader2 size={16} className="blog-btn-spin" />
                            ) : (
                                <Save size={16} />
                            )}
                            <span>{t("blog.editor.saveDraft")}</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => handleSave("published")}
                            disabled={loading}
                            className="blog-editor-btn-primary"
                        >
                            {loading && savingStatus === "published" ? (
                                <Loader2 size={16} className="blog-btn-spin" />
                            ) : (
                                <Send size={16} />
                            )}
                            <span>{canAutoApprove ? t("blog.editor.publishNow") : t("blog.editor.sendToReview")}</span>
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default BlogEditor;
